// src/utils/ViewReport/viewReportState.js

/**
 * State management for view-report data capture.
 * Mirrors the pattern from src/utils/Onboarding/onboardingState.js
 */

import { 
  createInitialViewReportFieldsState, 
  createEmptyViewReportFieldState, 
  getViewReportFieldDefinition, 
  VIEW_REPORT_FIELD_KEYS 
} from './viewReportFields.js';
import { generateViewReportSessionIdWithRequestId, getUtcIsoTimestamp } from './viewReportSession.js';

const FILLED_ONCE = 'filled_once';
const UPDATED_MULTI = 'updated_multiple_times';

function deriveStatus(fillCount) {
  if (fillCount <= 0) return 'never_filled';
  if (fillCount === 1) return FILLED_ONCE;
  return UPDATED_MULTI;
}

/**
 * Creates the initial view-report state structure.
 * 
 * @param {object} options
 * @param {string} options.requestId - The report request ID from URL
 * @param {string} options.createdAt - Optional creation timestamp
 * @param {string} options.sessionId - Optional pre-generated session ID
 */
export function createInitialViewReportState(options = {}) {
  const createdAt = options.createdAt ?? getUtcIsoTimestamp();
  const requestId = options.requestId || null;
  const sessionId = options.sessionId ?? generateViewReportSessionIdWithRequestId(requestId, createdAt);
  const fields = createInitialViewReportFieldsState();
  const latestValues = {};

  for (const key of VIEW_REPORT_FIELD_KEYS) {
    const field = fields[key];
    latestValues[key] = field.latestValue;
  }

  return {
    session: {
      id: sessionId,
      requestId: requestId,
      createdAt,
      lastUpdatedAt: createdAt,
    },
    fields,
    latestValues,
    dirtyFields: {},
    hasPendingChanges: false,
    sync: {
      lastSyncedAt: null,
      lastSyncAttemptAt: null,
      lastSyncError: null,
      firestoreDocumentId: null,
    },
  };
}

export function ensureViewReportFieldState(state, fieldKey) {
  if (state.fields[fieldKey]) return state.fields[fieldKey];
  const definition = getViewReportFieldDefinition(fieldKey);
  if (!definition) return null;
  const cloned = createEmptyViewReportFieldState(fieldKey);
  state.fields[fieldKey] = cloned;
  state.latestValues[fieldKey] = cloned.latestValue;
  return cloned;
}

/**
 * Applies field updates to the view-report state.
 * Same pattern as onboardingState.applyFieldUpdates
 */
export function applyViewReportFieldUpdates(state, updates = {}, options = {}) {
  if (!state) {
    throw new Error('applyViewReportFieldUpdates requires an initialized view-report state.');
  }
  if (!updates || typeof updates !== 'object') {
    return state;
  }

  const fieldEntries = Object.entries(updates);
  if (fieldEntries.length === 0) {
    return state;
  }

  const perFieldOptions = options.perField ?? {};
  const baseTimestamp = options.timestamp ?? getUtcIsoTimestamp();
  const baseSource = options.source ?? 'local';

  let hasChanges = false;
  const fields = { ...state.fields };
  const latestValues = { ...state.latestValues };
  const dirtyFields = { ...(state.dirtyFields || {}) };
  let sessionUpdatedAt = state.session?.lastUpdatedAt ?? state.session?.createdAt ?? baseTimestamp;

  for (const [fieldKey, rawValue] of fieldEntries) {
    const definition = getViewReportFieldDefinition(fieldKey);
    if (!definition) {
      continue;
    }

    const fieldOption = perFieldOptions[fieldKey] || {};
    const entryTimestamp = fieldOption.timestamp ?? baseTimestamp;
    const entrySource = fieldOption.source ?? baseSource;
    const entryMetadata = fieldOption.metadata ?? options.metadata ?? null;

    const previousField = fields[fieldKey] ?? createEmptyViewReportFieldState(fieldKey);

    const history = [...previousField.history, {
      value: rawValue,
      updatedAt: entryTimestamp,
      source: entrySource,
      metadata: entryMetadata,
    }];

    const fillCount = history.length;
    const status = deriveStatus(fillCount);
    const firstFilledAt = previousField.firstFilledAt ?? entryTimestamp;

    fields[fieldKey] = {
      ...previousField,
      history,
      latestValue: rawValue,
      latestUpdatedAt: entryTimestamp,
      lastValueSource: entrySource,
      firstFilledAt,
      fillCount,
      status,
    };

    latestValues[fieldKey] = rawValue;
    dirtyFields[fieldKey] = true;
    hasChanges = true;

    if (!sessionUpdatedAt || entryTimestamp > sessionUpdatedAt) {
      sessionUpdatedAt = entryTimestamp;
    }
  }

  if (!hasChanges) {
    return state;
  }

  return {
    ...state,
    session: {
      ...state.session,
      lastUpdatedAt: sessionUpdatedAt,
    },
    fields,
    latestValues,
    dirtyFields,
    hasPendingChanges: true,
    sync: {
      ...state.sync,
      lastSyncError: state.sync?.lastSyncError ?? null,
    },
  };
}

export function getLatestViewReportValues(state) {
  if (!state) return {};
  return { ...state.latestValues };
}

export function getViewReportFieldHistory(state, fieldKey) {
  if (!state) return [];
  const field = state.fields?.[fieldKey];
  return field ? [...field.history] : [];
}

export function markViewReportFieldsAsSynced(state, fieldKeys = null, options = {}) {
  if (!state) return state;
  const keysToClear = Array.isArray(fieldKeys) && fieldKeys.length > 0
    ? fieldKeys
    : Object.keys(state.dirtyFields || {});

  const dirtyFields = { ...(state.dirtyFields || {}) };
  for (const key of keysToClear) {
    delete dirtyFields[key];
  }

  const hasPendingChanges = Object.keys(dirtyFields).length > 0;
  const lastSyncedAt = options.lastSyncedAt ?? getUtcIsoTimestamp();
  const syncPatch = {
    ...state.sync,
    lastSyncedAt,
    lastSyncError: options.lastSyncError ?? null,
    lastSyncAttemptAt: options.lastSyncAttemptAt ?? lastSyncedAt,
    firestoreDocumentId: options.firestoreDocumentId ?? state.sync?.firestoreDocumentId ?? null,
  };

  return {
    ...state,
    dirtyFields,
    hasPendingChanges,
    sync: syncPatch,
  };
}

export function recordViewReportSyncError(state, error, options = {}) {
  if (!state) return state;
  const lastSyncAttemptAt = options.lastSyncAttemptAt ?? getUtcIsoTimestamp();
  return {
    ...state,
    sync: {
      ...state.sync,
      lastSyncError: error instanceof Error ? error.message : error ?? 'Unknown sync error',
      lastSyncAttemptAt,
    },
  };
}
